import { Injectable } from '@angular/core';

import { Observable, of, pipe } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';

import { ResponseWrapper } from '@library/common/http';

import { CacheService } from './cache.service';

import { ShipmentTypes, ShipmentModes, Country, OceanPort } from '@library/common/models';

interface ServiceLevels {
   name: string;
   shipType: string;
   mode: string;
   book: string;
}

const refTypes = {
   customer: [ 'PO', 'SKU', 'CC', 'SO', 'GL', 'S/N' ],
   '4pl': [ 'PO', 'SKU', 'CC', 'SO', 'GL', 'TRAILER #', 'ORDER #', 'SEAL #' ],
};

@Injectable({
   providedIn: 'root',
})
export class EnumsService {
   url = 'enum';

   _observables: any = {};

   constructor( private cache: CacheService ) { }

   prefetch( items: string[] ): void {
      items.map( i => this[i] && this[i]().subscribe().unsubscribe() );
   }

   servicelevels( shipType?: ShipmentTypes, mode?: ShipmentModes, book: boolean = false ): Observable<string[]> {
      return this._get( 'servicelevels', true ).pipe(
         map( s => s as any[] ),
         map(( slevels: ServiceLevels[] ) => slevels
            .filter( s => shipType ? s.shipType === shipType : true )
            .filter( s => mode ? s.mode === mode : true )
            .filter( s => book ? s.book : true )
            .map( s => s.name )
         ),
         map( s => [ ...new Set( s ) ]),
         shareReplay(),
      );
   }

   currencies(): Observable<string[]> {
      return this._get( 'currencies', true );
   }

   incoterms( mode?: ShipmentModes ): Observable<string[]> {
      return this._get( [ 'incoterms', mode ].filter( _ => _ ).join( '/' ), true );
   }

   attachmentTypes( shipType?: ShipmentTypes, mode?: ShipmentModes ): Observable<string[]> {
      return this._get( [ 'document_types', shipType, mode ].filter( _ => _ ).join( '/' ), true );
   }

   containerTypes(): Observable<string[]> {
      return this._get( 'container_types', true );
   }

   countries(): Observable<Country[]> {
      return this._get( 'countries', true ) as any;
   }

   oceanPorts(): Observable<OceanPort[]> {
      return this._get( 'ocean_ports', true ) as any;
   }

   refParties(): Observable<string[]> {
      return of([ 'shipper', 'consignee', 'bill_to', 'project_number', 'foreign_house_bill', 'serial_number' ]);
   }

   reftypes(portal: string = 'customer'): Observable<string[]> {
      return of( refTypes[portal] || refTypes['customer'] );
   }

   accessorials(): Observable<any> {
      return this._get( 'accessorials', true ) as any;
   }

   _get( type: string, useLambda?: boolean ): Observable<string[]> {
      return this.cache.request<ResponseWrapper<string>, string[]>({
         url: `${this.url}/${type}`,
         length: 1400,
         pipe: pipe(
            map( rw => rw.response.collection ),
            map( list => list.sort(( a, b ) => a < b ? -1 : a > b ? 1 : 0 )),
            catchError( _ => of([])),
         ),
         useLambda,
      });
   }

   _post( type: string, params: any ): Observable<string[]> {
      return this.cache.request<ResponseWrapper<string>, string[]>({
         url: `${this.url}/${type}`, params,
         length: 1400,
         pipe: pipe(
            map( rw => rw.response.collection ),
            map( list => list.sort(( a, b ) => a < b ? -1 : a > b ? 1 : 0 )),
            catchError( _ => of([])),
         ),
      });
   }
}
